import React, { useState, useEffect } from "react";
import {
    Box,
    Container,
    Tabs,
    Tab,
    Stack,
    Typography,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Alert,
    Skeleton,
    Tooltip,
    LinearProgress,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TablePagination,
    CircularProgress,
    Paper,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import TokenOutlinedIcon from "@mui/icons-material/TokenOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import MemoryOutlinedIcon from "@mui/icons-material/MemoryOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CustomHeader from "../../components/common/CustomHeader";
import CustomButton from "../../components/common/CustomButton";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import CustomCard from "../../components/common/CustomCard";
import { DotsLoader } from "../../components/common/spinLoader";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import CustomFilter from "../../components/common/CustomFilter";
import CustomInput from "../../components/common/CustomInput";
import { clientApi } from "../../api/tenantApi";
import { subscriptionApi } from "../../api/subscriptionApi";
import CMNB from "../../assets/images/merubot_logo.png";
import { useNavigate } from "react-router-dom";
import CheckoutModal from "../../components/common/CheckoutModal";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "../../contexts/AuthContext";

// import { Crown } from "lucide-react";


// ─────── Tab Panel Component ───────
// function TabPanel(props) {
//   const { children, value, index, ...other } = props;
//   return (
//     <div
//       role="tabpanel"
//       hidden={value !== index}
//       id={`tabpanel-${index}`}
//       {...other}
//     >
//       {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
//     </div>
//   );
// ─── Credit Usage History Filter Config ───


const tokenFilterConfig = [
    {
        key: "process_name",
        label: "Process Name",
        type: "text",
        placeholder: "e.g. jdextraction",
    },
    {
        key: "processed_by",
        label: "Processed By",
        type: "text",
        placeholder: "Email or user",
    },
    {
        key: "created_at",
        label: "Date Range",
        type: "date_range",
    },
];

const formatCreatedAt = (dateStr) => {
    if (!dateStr) return "—";
    let d = new Date(dateStr);
    if (isNaN(d.getTime()) && typeof dateStr === "string") {
        const parts = dateStr.split(" ")[0].split("-");
        if (parts.length === 3) {
            d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        }
    }
    return isNaN(d.getTime()) ? String(dateStr) : d.toLocaleString("en-IN");
};

// ─── Page statistics (from API — current page) ─────────────────────────
const CreditUsageStatistics = ({ statistics }) => {
    if (!statistics) return null;
    const items = [
        { label: "Total Records", value: statistics.total_records ?? 0 },
        { label: "Credits (this page)", value: statistics.current_page_total ?? statistics.total_credits_used ?? 0 },
        { label: "Avg per process", value: statistics.average_credits_per_process ?? 0 },
        { label: "Min credits", value: statistics.min_credits_used ?? 0 },
        { label: "Max credits", value: statistics.max_credits_used ?? 0 },
    ];
    return (
        <Box sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                Statistics for the current page
            </Typography>
            <Grid container spacing={2}>
                {items.map((item) => (
                    <Grid item xs={6} sm={4} md="auto" key={item.label}>
                        <Typography variant="caption" color="text.secondary">
                            {item.label}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                            {typeof item.value === "number" ? item.value.toLocaleString() : item.value}
                        </Typography>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

// ─── Credit usage history table ────────────────────────────────────────
const CreditUsageHistoryTable = ({ rows, rowOffset = 0 }) => {
    const pageTotal = rows.reduce((sum, row) => sum + (row.credit_used ?? 0), 0);

    return (
        <Stack spacing={2}>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700, width: "4%" }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Process</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Processed By</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Credits Used</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Previous</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Remaining</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">No credit usage records found.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row, i) => (
                                <TableRow key={row.id || `${rowOffset}-${i}`} hover>
                                    <TableCell>
                                        <Typography variant="caption" color="text.secondary">
                                            {rowOffset + i + 1}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {row.process_name || "—"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {row.processed_by || "—"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: "warning.main" }}>
                                            {Number(row.credit_used ?? 0).toLocaleString()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2">{Number(row.previous_credit ?? 0).toLocaleString()}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2">{Number(row.remaining_credit ?? 0).toLocaleString()}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{formatCreatedAt(row.created_at)}</Typography>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                        {rows.length > 0 && (
                            <TableRow sx={{ bgcolor: "action.hover" }}>
                                <TableCell colSpan={3}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        Page total
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {pageTotal.toLocaleString()}
                                    </Typography>
                                </TableCell>
                                <TableCell colSpan={3} />
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Stack>
    );
};

// ─── Purchase History Statistics ────────────────────────────────────
const PurchaseHistoryStatistics = ({ statistics }) => {
    if (!statistics) return null;
    const items = [
        { label: "Total Records", value: statistics.total_records ?? 0 },
        { label: "Total Spent", value: `₹${(statistics.total_amount_spent ?? 0).toLocaleString()}` },
        { label: "Avg Purchase", value: `₹${(statistics.average_purchase_amount ?? 0).toLocaleString()}` },
        { label: "Min Purchase", value: `₹${(statistics.min_purchase_amount ?? 0).toLocaleString()}` },
        { label: "Max Purchase", value: `₹${(statistics.max_purchase_amount ?? 0).toLocaleString()}` },
    ];
    return (
        <Box sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                Statistics for the current page
            </Typography>
            <Grid container spacing={2}>
                {items.map((item) => (
                    <Grid item xs={6} sm={4} md="auto" key={item.label}>
                        <Typography variant="caption" color="text.secondary">
                            {item.label}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                            {typeof item.value === "number" ? item.value.toLocaleString() : item.value}
                        </Typography>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

// ─── Purchase History Table ────────────────────────────────────────
const PurchaseHistoryTable = ({ rows, rowOffset = 0 }) => {
    const pageTotal = rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);

    return (
        <Stack spacing={2}>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700, width: "4%" }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Plan Price</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Amount Paid</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Currency</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Credits Purchased</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Previous Credit</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Total Credit</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Purchased At</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Valid To</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">No purchase records found.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row, i) => (
                                <TableRow key={row.id || `${rowOffset}-${i}`} hover>
                                    <TableCell>
                                        <Typography variant="caption" color="text.secondary">
                                            {rowOffset + i + 1}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.main" }}>
                                            ₹{Number(row.plan_price ?? 0).toLocaleString()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: "success.main" }}>
                                            ₹{Number(row.amount ?? 0).toLocaleString()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={row.currency || "INR"} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: "0.7rem" }} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: "warning.main" }}>
                                            {Number(row.credit_purchased ?? 0).toLocaleString()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2">{Number(row.previous_credit ?? 0).toLocaleString()}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{Number(row.total_credit ?? 0).toLocaleString()}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{formatCreatedAt(row.purchased_at)}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{formatCreatedAt(row.valid_to)}</Typography>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                        {rows.length > 0 && (
                            <TableRow sx={{ bgcolor: "action.hover" }}>
                                <TableCell colSpan={2}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        Page total
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        ₹{pageTotal.toLocaleString()}
                                    </Typography>
                                </TableCell>
                                <TableCell colSpan={6} />
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Stack>
    );
};

// ─── Threshold config ───────────────────────────────────────────────
const NEAR_EXHAUST_PCT = 80;

// ─── Helper: derive status from used / allowed ──────────────────────
const getPlanRowStatus = (used, allowed) => {
    if (!allowed || allowed === 0) return "ok";
    const pct = (used / allowed) * 100;
    if (pct >= 100) return "exhausted";
    if (pct >= NEAR_EXHAUST_PCT) return "near";
    return "ok";
};

// ─── Status badge chip ──────────────────────────────────────────────
const StatusChip = ({ status }) => {
    const map = {
        exhausted: { label: "Exhausted", color: "error", icon: <ErrorOutlineRoundedIcon sx={{ fontSize: 14 }} /> },
        near: { label: "Near Limit", color: "warning", icon: <WarningAmberRoundedIcon sx={{ fontSize: 14 }} /> },
        ok: { label: "OK", color: "success", icon: <CheckCircleOutlinedIcon sx={{ fontSize: 14 }} /> },
    };
    const { label, color, icon } = map[status] || map.ok;
    return (
        <Chip
            label={label}
            color={color}
            size="small"
            icon={icon}
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: "0.7rem" }}
        />
    );
};

// ─── Plan feature rows config ────────────────────────────────────────
const PLAN_ROWS = [
    {
        label: "Platform Credits",
        icon: <TokenOutlinedIcon sx={{ fontSize: 18, color: "primary.main" }} />,
        usedKey: "credit_used",
        allowedKey: "credit",
    },
    {
        label: "Recruiter Accounts",
        icon: <PeopleOutlinedIcon sx={{ fontSize: 18, color: "primary.main" }} />,
        usedKey: "created_recruiter_count",
        allowedKey: "allowed_recruiter_count",
    },
];

// ─── Credit Breakdown Config ──────────────────────────────────────────
const CREDIT_BREAKDOWN = [
    { label: "Job Upload", value: 15, icon: <DescriptionOutlinedIcon sx={{ fontSize: 20 }} /> },
    { label: "CV Upload", value: 15, icon: <CloudUploadIcon sx={{ fontSize: 20 }} /> },
    { label: "Candidate Matching", value: 15, icon: <HubOutlinedIcon sx={{ fontSize: 20 }} /> },
    { label: "Interview", value: 400, icon: <MicNoneOutlinedIcon sx={{ fontSize: 20 }} /> },
    { label: "Job Generation", value: 20, icon: <AutoAwesomeOutlinedIcon sx={{ fontSize: 20 }} /> },
];

// ─── Plan Usage Table ────────────────────────────────────────────────
const PlanUsageTable = ({ subscribedPlan, tenantUsage }) => {
    const anyExhausted = PLAN_ROWS.some(
        (r) => getPlanRowStatus(tenantUsage?.[r.usedKey] ?? 0, r.usedKey === "credit_used" ? (tenantUsage?.credit_purchased ?? 0) : subscribedPlan[r.allowedKey]) === "exhausted"
    );
    const anyNear = PLAN_ROWS.some((r) => {
        const used = tenantUsage?.[r.usedKey] ?? 0;
        const allowed = r.usedKey === "credit_used" ? (tenantUsage?.credit_purchased ?? 0) : (subscribedPlan[r.allowedKey] ?? 0);
        const pct = allowed > 0 ? (used / allowed) * 100 : 0;
        return pct >= 80;
    });

    return (
        <Stack spacing={2}>
            {/* Credit Breakdown - Single Line */}
            <Box sx={{ bgcolor: "background.paper", p: 1, borderRadius: 1, border: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", gap: 1, overflow: "auto" }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        pr: 1.5,
                        mr: 1,
                        borderRight: "1px solid",
                        borderColor: "divider",
                        bgcolor: "primary.50",
                        px: 1,
                        py: 0.5,
                        borderRadius: 0.5,
                    }}
                >
                    <Typography variant="text" sx={{ fontWeight: 650, fontSize: "0.8rem", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 0.3 }}>
                        <TokenOutlinedIcon sx={{ fontSize: 16, color: "primary.main" }} />
                        <Typography
                            sx={{
                                fontWeight: 650,
                                fontSize: "0.8rem",
                                whiteSpace: "nowrap",
                                color: "primary.main",
                            }}
                        >
                            Credit Breakdown:
                        </Typography>
                    </Typography>
                </Box>
                {CREDIT_BREAKDOWN.map((item) => (
                    <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 0.3, whiteSpace: "nowrap", px: 0.75, py: 0.5, bgcolor: "grey.50", borderRadius: 0.5, border: "1px solid", borderColor: "grey.200" }}>
                        <Box sx={{ color: "primary.main", display: "flex", alignItems: "center", fontSize: "0.65rem" }}>
                            {item.icon}
                        </Box>
                        <Typography variant="text" sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
                            {item.label}: {item.value}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Top-level banners */}
            {anyExhausted && (
                <Alert severity="error" icon={<ErrorOutlineRoundedIcon />} sx={{ fontWeight: 600 }}>
                    One or more plan limits are <strong>exhausted</strong>. Some features are disabled.
                    Please contact your administrator to upgrade the plan.
                </Alert>
            )}

            <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: "background.default" }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700, width: "30%" }}>Resource</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, width: "20%" }}>
                                Allocated
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, width: "20%" }}>
                                Utilized
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, width: "15%" }}>
                                Available
                            </TableCell>
                            <TableCell align="left" sx={{ fontWeight: 700, width: "15%" }}>
                                Status
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {PLAN_ROWS.map(({ label, icon, usedKey, allowedKey }) => {
                            const isCreditRow = usedKey === "credit_used";
                            const used = tenantUsage?.[usedKey] ?? 0;
                            const allowed = isCreditRow ? (tenantUsage?.credit_purchased ?? 0) : (subscribedPlan[allowedKey] ?? 0);
                            const balance = isCreditRow ? (tenantUsage?.credit_balance ?? 0) : (allowed - used);
                            const pct = allowed > 0 ? Math.min((used / allowed) * 100, 100) : 0;

                            const isDanger = pct >= 80;
                            const isExhausted = pct >= 100;
                            const barColor = isExhausted ? "error" : (isDanger ? "warning" : "success");

                            const formatValue = (val) => isCreditRow ? `${val}` : val;

                            return (
                                <TableRow key={usedKey} hover sx={isDanger ? { backgroundColor: isExhausted ? "rgba(244, 67, 54, 0.08)" : "rgba(255, 152, 0, 0.12)" } : {}}>
                                    <TableCell>
                                        <Stack direction="row" alignItems="center" spacing={1.5}>
                                            {icon}
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {label}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography variant="body2">{tenantUsage || !isCreditRow ? formatValue(allowed) : "—"}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 700,
                                            }}
                                        >
                                            {tenantUsage ? formatValue(used) : "—"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: balance < 0 ? "error.main" : "inherit" }}>
                                            {tenantUsage ? formatValue(balance) : "—"}
                                            {balance < 0 && <WarningAmberRoundedIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: "middle" }} />}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 120 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={pct}
                                                color={barColor}
                                                sx={{
                                                    flex: 1,
                                                    height: 8,
                                                    borderRadius: 99,
                                                    bgcolor: "grey.200",
                                                }}
                                            />
                                            <Typography variant="caption" sx={{ minWidth: 34, fontWeight: 600 }}>
                                                {pct.toFixed(0)}%
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {anyNear && !anyExhausted && (
                <Alert severity="warning" sx={{ fontWeight: 600 }}>
                    Purchase plan near to finish please upgrade the plan
                </Alert>
            )}

            {!tenantUsage && (
                <Alert severity="info" sx={{ mt: 1 }}>
                    ℹ️ No usage recorded yet. Start by creating a recruiter or uploading a JD!
                </Alert>
            )}
        </Stack>
    );
};

// ─── Credit Usage Table ───────────────────────────────────────────────
const CreditUsageTable = ({ tenantUsage }) => {
    if (!tenantUsage) {
        return (
            <Alert severity="info">No credit usage data available yet.</Alert>
        );
    }

    const creditUsed = tenantUsage?.credit_used ?? 0;
    const creditPurchased = tenantUsage?.credit_purchased ?? 0;
    const creditBalance = tenantUsage?.credit_balance ?? 0;
    const creditPercentage = creditPurchased > 0 ? Math.min((creditUsed / creditPurchased) * 100, 100) : 0;

    return (
        <Stack spacing={2}>
            <Box sx={{ bgcolor: "background.paper", p: 2, borderRadius: 1.5, border: "1px solid", borderColor: "divider" }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: "center" }}>
                            <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                                Total Purchased
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.main" }}>
                                {creditPurchased.toLocaleString()}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: "center" }}>
                            <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                                Used
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: "warning.main" }}>
                                {creditUsed.toLocaleString()}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: "center" }}>
                            <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                                Balance
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: creditBalance < 0 ? "error.main" : "success.main" }}>
                                {creditBalance.toLocaleString()}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: "center" }}>
                            <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                                Usage
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {creditPercentage.toFixed(1)}%
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            <Box>
                <LinearProgress
                    variant="determinate"
                    value={creditPercentage}
                    color={creditPercentage >= 100 ? "error" : creditPercentage >= 80 ? "warning" : "success"}
                    sx={{ height: 8, borderRadius: 4 }}
                />
            </Box>
        </Stack>
    );
};

const AdminSettingsPage = () => {
    const theme = useTheme();
    const { setAuthData, user_type } = useAuth();
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(true);
    const [logoLoading, setLogoLoading] = useState(false);
    const [planLoading, setPlanLoading] = useState(false);
    const [allPlans, setAllPlans] = useState([]);
    const [selectedPlanId, setSelectedPlanId] = useState("");
    const [requestingPlan, setRequestingPlan] = useState(false);
    const navigate = useNavigate();


    // Logo state
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(CMNB);

    // Plan state
    const [subscribedPlan, setSubscribedPlan] = useState(null);
    const [tenantUsage, setTenantUsage] = useState(null);


    // Token usage state (lazy-loaded on tab 2)
    const [baseTokenUsage, setBaseTokenUsage] = useState([]);
    const [tokenData, setTokenData] = useState(null); // tracking fetch status
    const [tokenLoading, setTokenLoading] = useState(false);

    // Pagination state
    const [pagination, setPagination] = useState({
        offset: 0,
        limit: 10,
        total_records: 0,
        has_more: false,
        current_page_size: 0
    });

    // Statistics state
    const [statistics, setStatistics] = useState(null);

    // Purchase History state (lazy-loaded on tab 2)
    const [purchaseHistory, setPurchaseHistory] = useState([]);
    const [purchaseData, setPurchaseData] = useState(null);
    const [purchaseLoading, setPurchaseLoading] = useState(false);
    const [purchaseStatistics, setPurchaseStatistics] = useState(null);
    const [purchasePagination, setPurchasePagination] = useState({
        offset: 0,
        limit: 10,
        total_records: 0,
        has_more: false,
        current_page_size: 0
    });

    // Topup Modal State
    const [isTopupModalOpen, setIsTopupModalOpen] = useState(false);
    const [selectedTopupPlanId, setSelectedTopupPlanId] = useState("");
    const [isUpgradingPlan, setIsUpgradingPlan] = useState(false);
    const [fetchingPlans, setFetchingPlans] = useState(false);

    // Checkout Modal State
    const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
    const [checkoutPlan, setCheckoutPlan] = useState(null);

    // Filter state
    const [tokenFilters, setTokenFilters] = useState([]);

    // Active Filter state for rendering
    const [activeTokenFilters, setActiveTokenFilters] = useState([]);

    const [tokenSearch, setTokenSearch] = useState("");

    const tokenValues = React.useMemo(() => {
        const obj = {};
        tokenFilters.forEach(({ property, value, operator }) => {
            if (operator) {
                obj[property] = { value, operator };
            } else {
                obj[property] = value;
            }
        });
        return obj;
    }, [tokenFilters]);

    // Use baseTokenUsage directly since server handles pagination/filtering
    const filteredTokens = React.useMemo(() => {
        return baseTokenUsage;
    }, [baseTokenUsage]);

    const handleFilterChange = (key, value, mode, operator = "equal") => {
        setTokenFilters((prev) => {
            const filtered = prev.filter((f) => f.property !== key);
            return [
                ...filtered,
                { property: key, value, operator }
            ];
        });
    };

    const handleApplyFilters = () => {
        setActiveTokenFilters(tokenFilters);
        setPagination((prev) => ({ ...prev, offset: 0 }));
        fetchTokenData(0, tokenFilters);
    };

    const handleClearFilters = () => {
        setTokenFilters([]);
        setActiveTokenFilters([]);
        setTokenSearch("");
        setPagination((prev) => ({ ...prev, offset: 0 }));
        fetchTokenData(0, []);
    };

    const handleTokenSearch = (value) => {
        setTokenSearch(value);
    };

    const handlePageChange = (_, newPage) => {
        const newOffset = newPage * pagination.limit;
        setPagination((prev) => ({ ...prev, offset: newOffset }));
        fetchTokenData(newOffset);
    };

    const handleRowsPerPageChange = (e) => {
        const newLimit = parseInt(e.target.value, 10);
        setPagination((prev) => ({ ...prev, limit: newLimit, offset: 0 }));
        fetchTokenData(0, null, newLimit);
    };

    // Snackbar state
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });


    // ─── Fetch Data on Mount ───
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                // Fetch logo
                const logoRes = await clientApi.getLogo();
                if (logoRes.status === 200 && logoRes.data) {
                    setLogoPreview(logoRes.data.data || CMNB);
                }

                // Fetch subscription details
                const subscriptionRes = await subscriptionApi.getTenantPlan();
                if (subscriptionRes.status === 200 && subscriptionRes.data) {
                    setSubscribedPlan(subscriptionRes.data.subscribed_plan);
                    setTenantUsage(subscriptionRes.data.tenant_usage);
                }
            } catch (error) {
                console.error("Error fetching settings:", error);
                setSnackbar({
                    open: true,
                    message: "Failed to fetch settings",
                    severity: "error",
                });
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Monitor plan limits to trigger CustomSnackbar
    useEffect(() => {
        if (!loading && !subscribedPlan) {
            setSnackbar({
                open: true,
                message: "Could not load plan details. Please try refreshing or contact your administrator.",
                severity: "warning"
            });
        }
    }, [loading, subscribedPlan]);



    // ─── Handle Tab Change + lazy-load token data ───
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        if (newValue === 1 && tokenData === null && !tokenLoading) {
            fetchTokenData(0);
        }
        if (newValue === 2 && purchaseData === null && !purchaseLoading) {
            fetchPurchaseHistory(0);
        }
    };

    const buildFilterParams = (filtersList) => {
        const filterParams = {};
        filtersList.forEach((filter) => {
            const val = filter.value;
            if (filter.property === "process_name" && val) {
                filterParams.process_name = typeof val === "string" ? val : val?.value ?? "";
            } else if (filter.property === "processed_by" && val) {
                filterParams.processed_by = typeof val === "string" ? val : val?.value ?? "";
            } else if (filter.property === "created_at" && val) {
                const range = val?.start != null ? val : val?.value ?? val;
                if (range?.start) filterParams.created_at_start = range.start;
                if (range?.end) filterParams.created_at_end = range.end;
            }
        });
        if (tokenSearch.trim()) {
            filterParams.search = tokenSearch.trim();
        }
        return filterParams;
    };

    // ─── Fetch credit usage history (admin — pagination + server filters) ───
    const fetchTokenData = async (offset = 0, filtersOverride = null, limitOverride = null) => {
        try {
            setTokenLoading(true);
            const filtersToUse = filtersOverride ?? activeTokenFilters;
            const limit = limitOverride ?? pagination.limit;
            const filterParams = { ...buildFilterParams(filtersToUse), userType: user_type };

            const res = await subscriptionApi.getAdminCreditUsage(offset, limit, filterParams);
            const { status, data, message } = res || {};

            if (status === 0) {
                setSnackbar({
                    open: true,
                    message: res.message || "Network error. Please check your connection.",
                    severity: "warning",
                });
                return;
            }

            if (status >= 400) {
                setSnackbar({
                    open: true,
                    message: message || "Failed to load credit usage history.",
                    severity: "error",
                });
                setTokenData({ usage_history: [] });
                setBaseTokenUsage([]);
                setStatistics(null);
                return;
            }

            const payload = status === 200 && data ? data : null;
            if (payload && typeof payload === "object") {
                setBaseTokenUsage(payload.usage_history || []);
                setStatistics(payload.statistics ?? null);
                if (payload.pagination) {
                    setPagination({
                        offset: payload.pagination.offset ?? offset,
                        limit: payload.pagination.limit ?? limit,
                        total_records: payload.pagination.total_records ?? 0,
                        has_more: payload.pagination.has_more ?? false,
                        current_page_size: payload.pagination.current_page_size ?? 0,
                        next_offset: payload.pagination.next_offset ?? null,
                    });
                }
                setTokenData(payload);
            } else {
                setTokenData({ usage_history: [] });
                setBaseTokenUsage([]);
                setStatistics(null);
            }
        } catch (err) {
            console.error("Credit usage fetch error:", err);
            setTokenData({ usage_history: [] });
            setBaseTokenUsage([]);
            setStatistics(null);
            setSnackbar({
                open: true,
                message: "Error loading credit usage history.",
                severity: "error",
            });
        } finally {
            setTokenLoading(false);
        }
    };

    // ─── Fetch purchase history (admin — pagination) ───
    const fetchPurchaseHistory = async (offset = 0, limitOverride = null) => {
        try {
            setPurchaseLoading(true);
            const limit = limitOverride ?? purchasePagination.limit;

            const res = await subscriptionApi.getPurchaseHistory(offset, limit, { userType: user_type });
            const { status, data, message } = res || {};

            if (status === 0) {
                setSnackbar({
                    open: true,
                    message: res.message || "Network error. Please check your connection.",
                    severity: "warning",
                });
                return;
            }

            if (status >= 400) {
                setSnackbar({
                    open: true,
                    message: message || "Failed to load purchase history.",
                    severity: "error",
                });
                setPurchaseData({ purchase_history: [] });
                setPurchaseHistory([]);
                setPurchaseStatistics(null);
                return;
            }

            const payload = status === 200 && data ? data : null;
            if (payload && typeof payload === "object") {
                setPurchaseHistory(payload.purchase_history || []);
                setPurchaseStatistics(payload.statistics ?? null);
                if (payload.pagination) {
                    setPurchasePagination({
                        offset: payload.pagination.offset ?? offset,
                        limit: payload.pagination.limit ?? limit,
                        total_records: payload.pagination.total_records ?? 0,
                        has_more: payload.pagination.has_more ?? false,
                        current_page_size: payload.pagination.current_page_size ?? 0,
                        next_offset: payload.pagination.next_offset ?? null,
                    });
                }
                setPurchaseData(payload);
            } else {
                setPurchaseData({ purchase_history: [] });
                setPurchaseHistory([]);
                setPurchaseStatistics(null);
            }
        } catch (err) {
            console.error("Purchase history fetch error:", err);
            setPurchaseData({ purchase_history: [] });
            setPurchaseHistory([]);
            setPurchaseStatistics(null);
            setSnackbar({
                open: true,
                message: "Error loading purchase history.",
                severity: "error",
            });
        } finally {
            setPurchaseLoading(false);
        }
    };

    const handlePurchasePageChange = (_, newPage) => {
        const newOffset = newPage * purchasePagination.limit;
        setPurchasePagination((prev) => ({ ...prev, offset: newOffset }));
        fetchPurchaseHistory(newOffset);
    };

    const handlePurchaseRowsPerPageChange = (e) => {
        const newLimit = parseInt(e.target.value, 10);
        setPurchasePagination((prev) => ({ ...prev, limit: newLimit, offset: 0 }));
        fetchPurchaseHistory(0, newLimit);
    };

    // Debounced search refetch on Credit Usage tab
    useEffect(() => {
        if (tabValue !== 1 || tokenData === null) return undefined;
        const timer = setTimeout(() => {
            setPagination((prev) => ({ ...prev, offset: 0 }));
            fetchTokenData(0);
        }, 400);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenSearch]);

    // ─── Handle Logo File Selection ───
    const handleLogoFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith("image/")) {
                setSnackbar({
                    open: true,
                    message: "Please select an image file",
                    severity: "error",
                });
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                setSnackbar({
                    open: true,
                    message: "File size must be less than 5MB",
                    severity: "error",
                });
                return;
            }

            setLogoFile(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setLogoPreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // ─── Handle Logo Upload ───
    const handleLogoUpload = async () => {
        if (!logoFile) {
            setSnackbar({
                open: true,
                message: "Please select a logo file first",
                severity: "warning",
            });
            return;
        }

        try {
            setLogoLoading(true);
            const response = await clientApi.uploadLogo(logoFile);

            if (response.status === 0) {
                setSnackbar({
                    open: true,
                    message: "🌐 Network error. Please check your internet connection.",
                    severity: "warning",
                });
                return;
            }

            if (response.status >= 400) {
                setSnackbar({
                    open: true,
                    message: response.message || "Failed to upload logo",
                    severity: "error",
                });
                return;
            }

            if (response.status === 200 || response.status === 201) {
                setSnackbar({
                    open: true,
                    message: "Logo uploaded successfully!",
                    severity: "success",
                });
                setLogoFile(null);
                if (response.data?.logo_url) {
                    setAuthData({ companyLogo: response.data.logo_url });
                    // Keep the event if other parts of the UI are listening to it without context (e.g. non-React components or old code)
                    window.dispatchEvent(new Event("logoUpdated"));
                }
                return;
            }
        } catch (error) {
            console.error("Logo upload error:", error);
            setSnackbar({
                open: true,
                message: "Error uploading logo",
                severity: "error",
            });
        } finally {
            setLogoLoading(false);
        }
    };

    // ─── Handle Plan Change Request ───
    const handleRequestPlanChange = async () => {
        if (!selectedPlanId) {
            setSnackbar({
                open: true,
                message: "Please select a plan first",
                severity: "warning",
            });
            return;
        }

        try {
            setRequestingPlan(true);
            // TODO: Call API to request plan change
            // await subscriptionApi.requestPlanChange(selectedPlanId);

            setSnackbar({
                open: true,
                message: "Plan change request sent! You will be notified soon.",
                severity: "success",
            });
            setSelectedPlanId("");
        } catch (error) {
            console.error("Plan change error:", error);
            setSnackbar({
                open: true,
                message: "Error requesting plan change",
                severity: "error",
            });
        } finally {
            setRequestingPlan(false);
        }
    };

    const handleTopupSubmit = async () => {
        if (!selectedTopupPlanId) {
            setSnackbar({ open: true, message: "Please select a plan", severity: "warning" });
            return;
        }
        // Clear any prior message before retrying
        setSnackbar({ open: false, message: "", severity: "info" });
        setIsUpgradingPlan(true);
        try {
            const response = await subscriptionApi.planChangeRequest(selectedTopupPlanId);
            if (response.status === 200 || response.status === 201 || response.success) {
                setSnackbar({ open: true, message: "Plan upgraded successfully", severity: "success" });
                setIsTopupModalOpen(false);
                setSelectedTopupPlanId("");

                // Refetch current plan so UI updates
                setLoading(true);
                const subscriptionRes = await subscriptionApi.getTenantPlan();
                if (subscriptionRes.status === 200 && subscriptionRes.data) {
                    setSubscribedPlan(subscriptionRes.data.subscribed_plan);
                    setTenantUsage(subscriptionRes.data.tenant_usage);
                }
                setLoading(false);
            } else {
                setSnackbar({ open: true, message: response.message || "Failed to upgrade plan", severity: "error" });
            }
        } catch (e) {
            setSnackbar({ open: true, message: "Error upgrading plan. Please try again.", severity: "error" });
        } finally {
            setIsUpgradingPlan(false);
        }
    };

    // // ─── LOGO TAB CONTENT ───
    // const LogoTabContent = () => (
    //   <Container maxWidth="sm" sx={{ mt: -4, mb: 2 }}>
    //     <CustomCard sx={{ p: 4, position: "relative" }}>
    //       <Stack spacing={3}>
    //         <Box>
    //           <Typography
    //             variant="h6"
    //             sx={{
    //               mb: 2,
    //               fontWeight: 600,
    //               display: "flex",
    //               alignItems: "center",
    //               gap: 1.5,
    //             }}
    //           >
    //             Company Logo
    //             <Tooltip title="This is a Premium Feature">
    //             <Box
    //               component="span"
    //               sx={{
    //                 position: "absolute",
    //                 top: 12,
    //                 right: 12,
    //                 display: "inline-flex",
    //                 alignItems: "center",
    //                 gap: 0.5,
    //                 px: 1,
    //                 py: 0.25,
    //                 borderRadius: "999px",
    //                 background:
    //                   "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)",
    //                 boxShadow: "0 2px 8px rgba(249,115,22,0.35)",
    //                 fontSize: "0.65rem",
    //                 fontWeight: 700,
    //                 letterSpacing: "0.05em",
    //                 color: "#fff",
    //                 textTransform: "uppercase",
    //                 lineHeight: 1,
    //               }}
    //             >
    //               <Crown size={21} strokeWidth={1.5} />
    //               {/* Premium */}
    //             </Box>
    //             </Tooltip>
    //           </Typography>
    //         </Box>

    //         <Divider />

    //         {/* Logo Preview */}
    //         <Box
    //           sx={{
    //             display: "flex",
    //             justifyContent: "center",
    //             p: 3,
    //             bgcolor: "background.default",
    //             borderRadius: 2,
    //             border: "2px dashed",
    //             borderColor: "divider",
    //             minHeight: 200,
    //             alignItems: "center",
    //           }}
    //         >
    //           <Box
    //             component="img"
    //             src={logoPreview}
    //             alt="Logo Preview"
    //             sx={{
    //               maxWidth: "100%",
    //               maxHeight: 180,
    //               objectFit: "contain",
    //             }}
    //           />
    //         </Box>

    //         {/* File Input */}
    //         <Box
    //           sx={{
    //             position: "relative",
    //             display: "inline-block",
    //             width: "100%",
    //           }}
    //         >
    //           <input
    //             type="file"
    //             accept="image/*"
    //             onChange={handleLogoFileChange}
    //             style={{ display: "none" }}
    //             id="logo-input"
    //             disabled={logoLoading}
    //           />
    //           <label htmlFor="logo-input" style={{ width: "100%" }}>
    //             <CustomButton
    //               fullWidth
    //               variant="outlined"
    //               startIcon={<CloudUploadIcon />}
    //               disabled={logoLoading}
    //               onClick={() => document.getElementById("logo-input").click()}
    //             >
    //               {logoLoading ? "Uploading..." : "Choose Logo File"}
    //             </CustomButton>
    //           </label>
    //         </Box>

    //         {/* Upload Button */}
    //         <CustomButton
    //           fullWidth
    //           variant="contained"
    //           disabled={!logoFile || logoLoading}
    //           onClick={handleLogoUpload}
    //         >
    //           {logoLoading ? <DotsLoader /> : "Upload Logo"}
    //         </CustomButton>

    //         <Typography variant="caption" color="text.secondary" align="center">
    //           Supported formats: JPG, PNG, SVG (Max 5MB)
    //         </Typography>
    //       </Stack>
    //     </CustomCard>
    //   </Container>
    // );

    // ─── PLAN TAB CONTENT ───




    // ─── MAIN RENDER ───
    return (
        <Box>
            {/* Header */}
            <CustomHeader title="Admin Settings" />

            <Container maxWidth="lg" sx={{ mt: 3, pb: 6 }}>
                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        aria-label="admin settings tabs"
                    >
                        <Tab
                            icon={<CardMembershipOutlinedIcon />}
                            iconPosition="start"
                            label="Plan"
                            id="tab-0"
                            sx={{
                                minHeight: 48,
                                borderRadius: 1,
                                transition: "all 0.2s ease-in-out",
                                "&:hover": {
                                    bgcolor: "action.hover",
                                    transform: "translateY(-1px)",
                                },
                            }}
                        />
                        <Tab
                            icon={<MemoryOutlinedIcon />}
                            iconPosition="start"
                            label="Credit Usage"
                            id="tab-1"
                            sx={{
                                minHeight: 48,
                                borderRadius: 1,
                                transition: "all 0.2s ease-in-out",
                                "&:hover": {
                                    bgcolor: "action.hover",
                                    transform: "translateY(-1px)",
                                },
                            }}
                        />
                        <Tab
                            icon={<ShoppingCartOutlinedIcon />}
                            iconPosition="start"
                            label="Purchase History"
                            id="tab-2"
                            sx={{
                                minHeight: 48,
                                borderRadius: 1,
                                transition: "all 0.2s ease-in-out",
                                "&:hover": {
                                    bgcolor: "action.hover",
                                    transform: "translateY(-1px)",
                                },
                            }}
                        />
                    </Tabs>
                </Box>

                <TabPanel value={tabValue} index={0}>
                    <Stack spacing={4}>
                        {/* Current Plan Section */}
                        <Box>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <SpeedOutlinedIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        Plan Usage
                                    </Typography>
                                </Box>
                                <CustomButton variant="contained" disabled={loading} onClick={async () => {
                                    setIsTopupModalOpen(true);
                                    setFetchingPlans(true);
                                    try {
                                        const allPlansRes = await subscriptionApi.getPlans({ role: "admin" });
                                        if (allPlansRes.status === 200 && allPlansRes.data) {
                                            const rawPlans = allPlansRes.data;
                                            const plansArray = Array.isArray(rawPlans)
                                                ? rawPlans
                                                : Object.values(rawPlans);
                                            setAllPlans(plansArray);
                                        } else {
                                            setSnackbar({ open: true, message: "Failed to fetch plans", severity: "error" });
                                        }
                                    } catch (error) {
                                        setSnackbar({ open: true, message: "Error fetching plans", severity: "error" });
                                    } finally {
                                        setFetchingPlans(false);
                                    }
                                }}>
                                    Change Plan
                                </CustomButton>
                            </Box>

                            {loading ? (
                                <SkeletonLoader type="table" rows={4} columns={[1, 2, 3, 4, 5]} />
                            ) : subscribedPlan ? (
                                <CustomCard sx={{ p: 3 }}>
                                    {/* Plan name pill */}
                                    <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Current Plan:
                                        </Typography>
                                        <Chip
                                            label={subscribedPlan.plan_name || "Unknown Plan"}
                                            color="primary"
                                            variant="filled"
                                            size="small"
                                            sx={{ fontWeight: 700 }}
                                        />
                                    </Box>
                                    <PlanUsageTable
                                        subscribedPlan={subscribedPlan}
                                        tenantUsage={tenantUsage}
                                    />
                                </CustomCard>
                            ) : (
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                                    No plan details available.
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    <Stack spacing={3}>
                        <Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                <HubOutlinedIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    Credit Usage
                                </Typography>
                            </Box>

                            <CustomCard sx={{ p: 3, mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                                    Account summary
                                </Typography>
                                <CreditUsageTable tenantUsage={tenantUsage} />
                            </CustomCard>

                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                                Usage history
                            </Typography>

                            <Box sx={{ mb: 2 }}>
                                <CustomFilter
                                    filtersConfig={tokenFilterConfig}
                                    values={tokenValues}
                                    onChange={handleFilterChange}
                                    onApply={handleApplyFilters}
                                    onClear={handleClearFilters}
                                    onSearch={handleTokenSearch}
                                    searchValue={tokenSearch}
                                />
                            </Box>

                            {tokenLoading ? (
                                <Stack spacing={1}>
                                    {[...Array(5)].map((_, i) => (
                                        <Skeleton
                                            key={i}
                                            variant="rectangular"
                                            height={48}
                                            sx={{ borderRadius: 1 }}
                                        />
                                    ))}
                                </Stack>
                            ) : tokenData === null ? (
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                                    Loading credit usage history...
                                </Typography>
                            ) : (
                                <CustomCard sx={{ p: 3, overflow: "hidden" }}>
                                    <Stack spacing={2}>
                                        <CreditUsageStatistics statistics={statistics} />
                                        <CreditUsageHistoryTable
                                            rows={filteredTokens}
                                            rowOffset={pagination.offset}
                                        />
                                        {pagination.total_records > 0 && (
                                            <TablePagination
                                                component="div"
                                                count={pagination.total_records}
                                                page={Math.floor(pagination.offset / pagination.limit)}
                                                onPageChange={handlePageChange}
                                                rowsPerPage={pagination.limit}
                                                onRowsPerPageChange={handleRowsPerPageChange}
                                                rowsPerPageOptions={[5, 10, 25, 50]}
                                                labelDisplayedRows={({ from, to, count }) =>
                                                    `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`
                                                }
                                            />
                                        )}
                                    </Stack>
                                </CustomCard>
                            )}
                        </Box>
                    </Stack>
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                    <Stack spacing={3}>
                        <Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                <ShoppingCartOutlinedIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    Purchase History
                                </Typography>
                            </Box>

                            {purchaseLoading ? (
                                <Stack spacing={1}>
                                    {[...Array(5)].map((_, i) => (
                                        <Skeleton
                                            key={i}
                                            variant="rectangular"
                                            height={48}
                                            sx={{ borderRadius: 1 }}
                                        />
                                    ))}
                                </Stack>
                            ) : purchaseData === null ? (
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                                    Loading purchase history...
                                </Typography>
                            ) : (
                                <CustomCard sx={{ p: 3, overflow: "hidden" }}>
                                    <Stack spacing={2}>
                                        <PurchaseHistoryStatistics statistics={purchaseStatistics} />
                                        <PurchaseHistoryTable
                                            rows={purchaseHistory}
                                            rowOffset={purchasePagination.offset}
                                        />
                                        {purchasePagination.total_records > 0 && (
                                            <TablePagination
                                                component="div"
                                                count={purchasePagination.total_records}
                                                page={Math.floor(purchasePagination.offset / purchasePagination.limit)}
                                                onPageChange={handlePurchasePageChange}
                                                rowsPerPage={purchasePagination.limit}
                                                onRowsPerPageChange={handlePurchaseRowsPerPageChange}
                                                rowsPerPageOptions={[5, 10, 25, 50]}
                                                labelDisplayedRows={({ from, to, count }) =>
                                                    `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`
                                                }
                                            />
                                        )}
                                    </Stack>
                                </CustomCard>
                            )}
                        </Box>
                    </Stack>
                </TabPanel>
            </Container>
            {/* Snackbar */}
            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            />

            {/* Topup Credit Modal */}
            <Dialog
                open={isTopupModalOpen}
                maxWidth="sm"
                fullWidth
                onClose={() => {
                    if (!isUpgradingPlan && !fetchingPlans) {
                        setIsTopupModalOpen(false);
                        setSelectedTopupPlanId("");
                        setSnackbar({ open: false, message: "", severity: "info" });
                    }
                }}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        p: 1
                    },
                }}
            >
                <DialogTitle
                    variant="h5"
                    align="center"
                    sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        pb: 0.3,
                    }}>
                    Subscribe to Plan
                </DialogTitle>
                <Typography
                    variant="body2"
                    sx={{
                        color: "text.secondary",
                        textAlign: "center",
                        mb: 1,
                        fontSize: "0.85rem",
                    }}>
                    Select the plan you want to subscribe to.
                </Typography>
                <Divider />

                <DialogContent>
                    {fetchingPlans ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <CircularProgress size={22} />
                            <Typography variant="body2" color="text.secondary">Fetching available plans...</Typography>
                        </Box>
                    ) : (
                        <Box>
                            <CustomInput
                                select
                                label="Select Plan"
                                name="select_plan"
                                size="small"
                                value={selectedTopupPlanId}
                                required
                                placeholder="Select plan name, eg. Starter"
                                disabled={allPlans.length === 0}
                                onChange={(e) => {
                                    setSelectedTopupPlanId(e.target.value);
                                    // Clear prior error when user selects a different plan
                                    setSnackbar({ open: false, message: "", severity: "info" });
                                }}
                            >
                                {allPlans.length === 0 ? (
                                    <MenuItem disabled value="">
                                        <em>No plans available to change</em>
                                    </MenuItem>
                                ) : (
                                    allPlans.map((plan) => (
                                        <MenuItem key={plan.id} value={plan.id}>
                                            {plan.plan_name}
                                        </MenuItem>
                                    ))
                                )}
                            </CustomInput>

                            {selectedTopupPlanId && (() => {
                                const selectedPlan = allPlans.find(p => p.id === selectedTopupPlanId);
                                if (!selectedPlan) return null;
                                return (
                                    <Box sx={{ mt: 3, p: 2, bgcolor: "grey.50", borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>Plan Features:</Typography>
                                        <Stack spacing={1.5}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Credits:</Typography>
                                                <Typography variant="body2" fontWeight="600">{selectedPlan.credit} </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Recruiters Allowed:</Typography>
                                                <Typography variant="body2" fontWeight="600">{selectedPlan.allowed_recruiter_count}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Plan Validity:</Typography>
                                                <Typography variant="body2" fontWeight="600">{selectedPlan.plan_expired_in_days ? `${selectedPlan.plan_expired_in_days} days` : "No Validity"}</Typography>
                                            </Box>
                                            <Divider />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Price:</Typography>
                                                <Typography variant="body2" fontWeight="700" color="primary.main">
                                                    ₹{selectedPlan.price}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Box>
                                );
                            })()}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions
                    sx={{
                        px: 3,
                        pb: 3,
                        display: "flex",
                        flexDirection: "row",
                        gap: 2,
                    }}>
                    <CustomButton
                        variant="outlined"
                        sx={{ flex: 1 }}
                        onClick={() => {
                            setIsTopupModalOpen(false);
                            setSelectedTopupPlanId("");
                            setSnackbar({ open: false, message: "", severity: "info" });
                        }} disabled={isUpgradingPlan}>
                        Cancel
                    </CustomButton>
                    <CustomButton
                        variant="contained"
                        sx={{ flex: 1 }}
                        disabled={isUpgradingPlan || !selectedTopupPlanId || allPlans.length === 0}
                        onClick={() => {
                            const selectedPlan = allPlans.find(p => p.id === selectedTopupPlanId);
                            setIsTopupModalOpen(false);
                            setCheckoutPlan(selectedPlan);
                            setIsCheckoutModalOpen(true);
                        }}
                    >
                        {isUpgradingPlan ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <CircularProgress size={20} color="inherit" />
                                Subscribing...
                            </span>
                        ) : "Subscribe Plan"}
                    </CustomButton>
                </DialogActions>
            </Dialog>

            <CheckoutModal
                open={isCheckoutModalOpen}
                onClose={() => {
                    setIsCheckoutModalOpen(false);
                    setCheckoutPlan(null);
                    setSelectedTopupPlanId("");
                }}
                plan={checkoutPlan}
                onSuccess={async () => {
                    setSnackbar({ open: true, message: "Plan upgraded successfully", severity: "success" });
                    setLoading(true);
                    try {
                        const subscriptionRes = await subscriptionApi.getTenantPlan();
                        if (subscriptionRes.status === 200 && subscriptionRes.data) {
                            setSubscribedPlan(subscriptionRes.data.subscribed_plan);
                            setTenantUsage(subscriptionRes.data.tenant_usage);
                        }
                    } catch (e) {
                        console.error(e);
                    } finally {
                        setLoading(false);
                    }
                }}
            />
        </Box>
    );
};

export default AdminSettingsPage;

// ─────── Tab Panel Component ───────
function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}
